import PropTypes from 'prop-types';
import classNames from 'classnames';
import React, { forwardRef } from 'react';
import BaseButton from './BaseButton';
import { ButtonType, ButtonVariant, ButtonSize } from './Button.type';

/**
 * @deprecated Use `Button` from `@dextech-ai/dex-design-system` instead.
 * Do not add new call sites.
 */

const buttonProperties = {
	[ButtonType.SOLID]: {
		[ButtonVariant.NEUTRAL]: {
			className: 'DS__button-solid DS__button-solid--neutral'
		},
		[ButtonVariant.DESTRUCTIVE]: {
			className: 'DS__button-solid DS__button-solid--destructive'
		},
		[ButtonVariant.STANDARD]: {
			className: 'DS__button-solid DS__button-solid--standard'
		}
	},
	[ButtonType.GHOST]: {
		[ButtonVariant.NEUTRAL]: {
			className: 'DS__button-ghost DS__button-ghost--neutral'
		},
		[ButtonVariant.DESTRUCTIVE]: {
			className: 'DS__button-ghost DS__button-ghost--destructive'
		},
		[ButtonVariant.STANDARD]: {
			className: 'DS__button-ghost DS__button-ghost--standard'
		}
	},
	[ButtonType.UTILITY]: {
		[ButtonVariant.UTILITY]: {
			className: 'DS__button-utility'
		}
	},
	[ButtonType.CHIP]: {
		[ButtonVariant.CHIP]: {
			className: 'DS__button-chip'
		}
	},
	[ButtonType.LINK]: {
		[ButtonVariant.STANDARD]: {
			className: 'DS__button-link DS__button-link--standard'
		}
	}
};

const Button = forwardRef(
(
	{
		dataTestId = 'button',
		type,
		variant,
		text,
		prefixIcon,
		suffixIcon,
		onClick,
		disabled,
		tooltipText,
		size,
		fullWidth = false,
		id,
		...buttonProps
	},
	ref
) => {
	const buttonConfig = buttonProperties[type]?.[variant];

	if (!buttonConfig) {
		console.error(`Variant ${variant} is not supported for button type ${type}.`);
		return `Variant ${variant} is not supported for type ${type}`;
	}

	const { className } = buttonConfig;

	const buttonClass = classNames(className, {
		'DS__button--small': size === ButtonSize.SMALL,
		'DS__button--default': size === ButtonSize.DEFAULT,
		'DS__button_icon--small':
			size === ButtonSize.SMALL &&
			(type === ButtonType.SOLID_ICON || type === ButtonType.GHOST_ICON),
		'DS__button-link': type === ButtonType.LINK,
		[`${className}--disabled`]: disabled
	});

	const handleButtonClick = () => {
		if (onClick) {
			const event = new MouseEvent('click', {
				bubbles: true,
				cancelable: true
			});
			onClick(event);
	}
	};

	return (
		<BaseButton
			ref={ref}
			dataTestId={dataTestId}
			text={text}
			prefixIcon={prefixIcon}
			suffixIcon={suffixIcon}
			onClick={handleButtonClick}
			btnClassName={buttonClass}
			disabled={disabled}
			tooltipText={tooltipText}
			btnType={type}
			fullWidth={fullWidth}
			id={id}
			{...buttonProps}
		/>
	);
});

Button.displayName = 'Button';

Button.propTypes = {
	dataTestId: PropTypes.string,
	type: PropTypes.oneOf(Object.values(ButtonType)).isRequired,
	variant: PropTypes.oneOf(Object.values(ButtonVariant)).isRequired,
	text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
	prefixIcon: PropTypes.node,
	suffixIcon: PropTypes.node,
	onClick: PropTypes.func,
	disabled: PropTypes.bool,
	tooltipText: PropTypes.string,
	size: PropTypes.oneOf(Object.values(ButtonSize)),
	fullWidth: PropTypes.bool,
	id: PropTypes.string
};

export default Button;
